import {CONFIG} from '../appConfig';
import STATE from '../constants/state';

/**
 * @namespace "services.solr"
 */
export default {
  /**
   * @desc Return all locations
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * services.solr.all();
   * // dispatched results stored in `state.gmi.solr.all`
   */
  all(data = {}) {
    const {params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `all`,
      params,
      statePath: STATE.SOLR.ALL
    };
  },

  /**
   * @desc Pass in country to return country locations
   * @param {Object} data {endpoint:{country}, params}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * const data = {
   *  endpoint: {
   *    country // *(String)* i.e. 'US'
   *  }
   * };
   * services.solr.country(data);
   * // dispatched results stored in `state.gmi.solr.country[country]`
   */
  country(data = {}) {
    const {endpoint = {}, params = {}} = data;
    const country = endpoint.country;
    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `country/${country}`,
      params,
      statePath: STATE.SOLR.COUNTRY.concat([country])
    };
  },

  /**
   * @desc Pass in country and state to return state locations
   * @param {Object} data {endpoint:{country, state}, params}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * const data = {
   *  endpoint: {
   *    country, // *(String)* i.e. 'US'
   *    state // *(String)* i.e. 'NY'
   *  }
   * };
   * services.solr.countryState(data);
   * // dispatched results stored in `state.gmi.solr.country_state[country][state]`
   */
  countryState(data = {}) {
    const {endpoint = {}, params = {}} = data;
    const {country, state} = endpoint;
    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `country/${country}/state/${state}`,
      params,
      statePath: STATE.SOLR.COUNTRY_STATE.concat([country, state])
    };
  },

  /**
   * @desc Pass in peopleSoftId with date bounds to return hours of operation
   * @param {Object} data {endpoint:{peopleSoftId}, params:{from, to}}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * const data = {
   *  endpoint: {
   *    peopleSoftId, // *(String)* i.e. '123254243'
   *  },
   *  params: {
   *    from, // *(String)* i.e. '2018-04-15'
   *    to // *(String)* i.e. '2018-04-22'
   *  }
   * };
   * services.solr.hours(data);
   * // dispatched results stored in `state.gmi.solr.hours[peopleSoftId]`
   */
  hours(data = {}){
    const {
      endpoint = {},
      params = {},
      fullResponse,
      cancelFn
    } = data;

    const peopleSoftId = endpoint.peopleSoftId;

    return {
      method: 'GET', 
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `hours/${peopleSoftId}`,
      params, 
      fullResponse,
      cancelFn,
      statePath: STATE.SOLR.HOURS.concat([peopleSoftId])
    };
  },

  /**
   * @desc Pass in a list of peopleSoftIds as payload with date bounds to return hours of operation
   * @param {Object} data {payload:{peopleSoftIds}, params:{from, to}}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * const data = {
   *   payload: {
   *      peopleSoftIds:[
   *       '1001368',
   *       '1001318',
   *       '1001345',
   *       '1008997',
   *       '1001343'
   *      ]
   *   },
   *  params: {
   *    from, // *(String)* i.e. '2018-04-15'
   *    to // *(String)* i.e. '2018-04-22'
   *  }
   * };
   * services.solr.allHours(data);
   * // will not store anything to state, use actions.solr.getAllHours(peopleSoftIds) to store details in state.gmi.solr.hours[peopleSoftId
   */
  allHours(data = {}){
    const {
      payload = {},
      params = {}
    } = data;

    return {
      method: 'POST', 
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: 'hours',
      data: payload,
      params
    };
  },



  /**
   * @desc Pass in a country code to return default age range
   * @param {Object} data {endpoint:{country}}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * const data = {
   *  endpoint: {
   *    country // *(String)* i.e. 'US'
   *  }
   * };
   * services.solr.renterAge(data);
   * // dispatched results stored in `state.gmi.solr.renter_age[country]`
   */
  renterAge(data = {}) {
    const {endpoint = {}, params = {}} = data;
    const {country = CONFIG.COUNTRY} = endpoint;
    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `renterage/country/${country}`,
      params,
      statePath: STATE.SOLR.RENTER_AGE.concat([country])
    };
  },

  /**
   * @desc Pass in a branch id to return age range results. 
   * This function differs from 'renterAge()' above - it calls the 'renterage' endpoint based on branch id (not country).
   * It was added because Enterprise Web requires age range results based on the individual branch.
   * @param {Object} data {endpoint:{branchId}}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * 
   * const data = {
   *  endpoint: {
   *    branchId: // *(String)* i.e. '1018860'
   *  }
   * };
   * services.solr.renterAgeBranch(data);
   * // dispatched results stored in `state.gmi.solr.renter_age[branchId]`
   */
  renterAgeBranch(data = {}) {
    const {endpoint = {}, params = {}} = data;
    const {branchId} = endpoint;
    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `renterage/${branchId}`,
      params,
      statePath: STATE.SOLR.RENTER_AGE.concat([branchId])
    };
  },

  /**
   * @desc Pass in an object representing a query string; get branch results and radius used
   * @param {Object} data {params: {latitude, longitude[, tribrand, radius]}}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * const data = {
   *  params: {
   *    latitude, // *(String)*
   *    rows, // *(String)* 20
   *    longitude, // *(String)*
   *    tribrand, // *(Boolean)* Optional. include all EHI brands or not
   *    radius // *(String)* Optional. Radius in KM
   *    dropoffDate // *(String)* Optional. 2023-03-09
   *    dropoffTime // *(String)* Optional. 12:00
   *    pickupDate // *(String)* Optional. 2023-03-09
   *    pickupTime // *(String)* Optional. 12:0
   *  },
   *  cancelFn // *(Function)* Optional.
   * };
   * services.solr.spatial(data);
   * // dispatched results stored in `state.gmi.solr.spatial`
   */
  spatial(data = {}){
    const {params = {}, cancelFn} = data;
    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `spatial`,
      params,
      cancelFn,
      statePath: STATE.SOLR.SPATIAL
    };
  },

  /**
   * @desc Pass in query string; get possible cities, branches, airports and ports of call
   * @param {Object} data {endpoint:{query}}
   * @returns {Object}
   * @memberof services.solr
   * @example
   * const data = {
   *  endpoint: {
   *    query // *(String)* i.e. 'STL'
   *  }
   * };
   * services.solr.text(data);
   * // dispatched results stored in `state.gmi.solr.text`
   */
  text(data = {}){
    const {endpoint = {}, params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `text/${endpoint.query}`,
      params,
      statePath: STATE.SOLR.TEXT
    };
  },

  /**
   * @name branches 
   * @desc Pass in method of delivery string; get a list of locations for a given method of delivery
   * @param {Object} data {methodOfDelivery} // Param is required, but if we send an empty param, we'll receive a list of locations with at least one available method of delivery
   * @returns {Object}
   * @memberof services.solr
   * @example
   * Methods of delivery available:
   * EA: Emerald Aisle
   * ER: Emerald Reserve Service
   * CH: Emerald Club Counter
   * FT: Priority Service
   * 
   * const data = {
   *   methodOfDelivery:  // *(String)* i.e 'EA' for Emerald Aisle
   * };
   * services.solr.branches(data);
   * // dispatched results stored in `state.gmi.solr.method_of_delivery[methodOfDelivery]`
   */
  branches(data = {}) {
    const { methodOfDelivery = ''} = data;

    return {
      method: 'GET',
      origin: CONFIG.SOLR_ORIGIN_URL,
      endpoint: `branches?methodOfDelivery=${methodOfDelivery}`,
      statePath: STATE.SOLR.METHOD_OF_DELIVERY.concat(methodOfDelivery)
    };
  }
};
